// @core/stores/legalentity/LegalentityStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { legalentityApi } from '@core/api';
import type {
  ChangeLegalEntityStatusRequest,
  CreateLegalEntityRequest,
  LegalEntityResponse,
  ListLegalEntitiesResponse,
  LookupByIdentifierResponse,
  SetDefaultLegalEntityRequest,
  UpdateDefaultSignatoryRequest,
  UpdateLegalEntityRequest,
  VerifyLegalEntityRequest,
} from '@core/types/legalentity';

class LegalentityStore {

  // Loading states
  isLoading = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.error = null;
  }

  /**
   * Change legal entity status
   */
  async ChangeLegalEntityStatus(legal_entity_id: string, data: ChangeLegalEntityStatusRequest): Promise<LegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.ChangeLegalEntityStatus(legal_entity_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a legal entity
   */
  async CreateLegalEntity(data: CreateLegalEntityRequest): Promise<LegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.CreateLegalEntity(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete (archive) legal entity
   */
  async DeleteLegalEntity(legal_entity_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.DeleteLegalEntity(legal_entity_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get legal entity by ID
   */
  async GetLegalEntity(legal_entity_id: string): Promise<LegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.GetLegalEntity(legal_entity_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List legal entities
   */
  async ListLegalEntities(params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListLegalEntitiesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.ListLegalEntities(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Look up requisites by tax/registration ID
   */
  async LookupByIdentifier(params?: { country?: string | number | boolean; id?: string | number | boolean; kind?: string | number | boolean; }): Promise<LookupByIdentifierResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.LookupByIdentifier(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set as tenant's default legal entity
   */
  async SetDefaultLegalEntity(legal_entity_id: string, data: SetDefaultLegalEntityRequest): Promise<LegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.SetDefaultLegalEntity(legal_entity_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update default signatory
   */
  async UpdateDefaultSignatory(legal_entity_id: string, data: UpdateDefaultSignatoryRequest): Promise<LegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.UpdateDefaultSignatory(legal_entity_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update legal entity
   */
  async UpdateLegalEntity(legal_entity_id: string, data: UpdateLegalEntityRequest): Promise<LegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.UpdateLegalEntity(legal_entity_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Verify legal entity requisites
   */
  async VerifyLegalEntity(legal_entity_id: string, data: VerifyLegalEntityRequest): Promise<LegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await legalentityApi.VerifyLegalEntity(legal_entity_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const legalentityStore = new LegalentityStore();
